import React from 'react';
import { tableCellBorderWidth, tableCellMinWidth } from '@atlaskit/editor-common/styles';
import { akEditorTableNumberColumnWidth, akEditorTableLegacyCellMinWidth } from '@atlaskit/editor-shared-styles';
import { getTableContainerWidth } from '@atlaskit/editor-common/node-width';
import { isTableResizingEnabled } from '../table';
import { getBooleanFF } from '@atlaskit/platform-feature-flags';
import { useFeatureFlags } from '../../../use-feature-flags';
// we allow scaling down column widths by no more than 30%
// this intends to reduce unwanted scrolling in the Renderer in these scenarios:
// User A creates a table with column widths → User B views it on a smaller screen
// User A creates a table with column widths → User A views it with reduced viewport space (eg. Confluence sidebar is open)
const MAX_SCALING_PERCENT = 0.3;
const isTableResized = columnWidths => {
  const filteredWidths = columnWidths.filter(width => width !== 0);
  return !!filteredWidths.length;
};
const fixColumnWidth = (columnWidth, _tableWidth, _layoutWidth, zeroWidthColumnsCount, scaleDownPercent) => {
  if (columnWidth === 0) {
    return columnWidth;
  }

  // If the tables total width (including no zero widths col or cols without width) is less than the current layout
  // We scale up the columns to meet the minimum of the table layout.
  if (zeroWidthColumnsCount === 0 && scaleDownPercent) {
    return Math.max(Math.floor((1 - scaleDownPercent) * columnWidth), tableCellMinWidth);
  }
  return Math.max(
  // We need to take tableCellBorderWidth, to avoid unnecessary overflow.
  columnWidth - tableCellBorderWidth, zeroWidthColumnsCount ? akEditorTableLegacyCellMinWidth : tableCellMinWidth);
};
export const calcScalePercent = ({
  renderWidth,
  tableWidth,
  maxScale
}) => {
  const diffPercent = 1 - renderWidth / tableWidth;
  return diffPercent < maxScale ? diffPercent : maxScale;
};
const colWidthSum = columnWidths => columnWidths.reduce((prev, curr) => curr + prev, 0);
const renderScaleDownColgroup = props => {
  var _props$tableNode;
  let {
    columnWidths,
    isNumberColumnEnabled,
    renderWidth,
    tableNode,
    rendererAppearance,
    isInsideOfBlockNode,
    isinsideMultiBodiedExtension,
    isTableScalingEnabled
  } = props;
  if (!columnWidths) {
    return [];
  }
  const tableResized = isTableResized(columnWidths);
  const noOfColumns = columnWidths.length;
  let targetWidths;
  const tableContainerWidth = getTableContainerWidth(tableNode);
  if (isTableResizingEnabled(rendererAppearance) && !isInsideOfBlockNode && !isinsideMultiBodiedExtension && !tableResized) {
    // for tables with no column widths defined, assume that the real table width
    // is defined by node.attrs.width
    const tableWidth = (isNumberColumnEnabled ? tableContainerWidth - akEditorTableNumberColumnWidth : tableContainerWidth) - 1;
    const defaultColumnWidth = tableWidth / noOfColumns;
    targetWidths = new Array(noOfColumns).fill(defaultColumnWidth);
  } else if (!tableResized) {
    return null;
  }
  const sumOfColumns = colWidthSum(columnWidths);

  // A bug was released that caused col resizing in editor to be off by 2px, so using 2 here
  const isTableSmallerThanContainer = sumOfColumns < tableContainerWidth - 2;
  const forceScaleForNumColumn = getBooleanFF('platform.editor.scale-table-when-number-column-in-table-resized_y4qh2') && isTableScalingEnabled && isNumberColumnEnabled && tableResized &&
  // if table col widths are smaller than container, then ignore. Most likely was created before custom table widths, where
  // col widths could be smaller than container width (width was enforced by breakout buttons)
  !isTableSmallerThanContainer;

  // when table resized and number column is enabled, we need to scale down the table in render
  if (forceScaleForNumColumn) {
    const scalePercentage = +((tableContainerWidth - akEditorTableNumberColumnWidth) / tableContainerWidth).toFixed(2);
    const targetMaxWidth = tableContainerWidth - akEditorTableNumberColumnWidth;
    let totalWidthAfterScale = 0;
    const newScaledTargetWidths = columnWidths.map(width => {
      const newWidth = Math.floor(width * scalePercentage);
      totalWidthAfterScale += newWidth;
      return newWidth;
    });
    const diff = targetMaxWidth - totalWidthAfterScale;
    targetWidths = newScaledTargetWidths;
    if (diff > 0 || diff < 0 && Math.abs(diff) < tableCellMinWidth) {
      let updated = false;
      targetWidths = targetWidths.map(width => {
        if (!updated && width + diff > tableCellMinWidth) {
          updated = true;
          width += diff;
        }
        return width;
      });
    }
  }
  targetWidths = targetWidths || columnWidths;

  // @see ED-6056
  const maxTableWidth = renderWidth < tableContainerWidth ? renderWidth : tableContainerWidth;
  let tableWidth = isNumberColumnEnabled ? akEditorTableNumberColumnWidth : 0;
  let minTableWidth = tableWidth;
  let zeroWidthColumnsCount = 0;
  targetWidths.forEach(width => {
    if (width) {
      tableWidth += Math.ceil(width);
    } else {
      zeroWidthColumnsCount += 1;
    }
    minTableWidth += Math.ceil(width) || akEditorTableLegacyCellMinWidth;
  });
  let cellMinWidth = 0;
  let scaleDownPercent = 0;
  const isTableWidthFixed = getBooleanFF('platform.editor.table.preserve-widths-with-lock-button') && ((_props$tableNode = props.tableNode) === null || _props$tableNode === void 0 ? void 0 : _props$tableNode.attrs.displayMode) === 'fixed';

  // fixes migration tables with zero-width columns
  if (zeroWidthColumnsCount > 0) {
    if (minTableWidth > maxTableWidth) {
      const minWidth = Math.ceil((maxTableWidth - tableWidth) / zeroWidthColumnsCount);
      cellMinWidth = minWidth < akEditorTableLegacyCellMinWidth ? akEditorTableLegacyCellMinWidth : minWidth;
    }
  }
  // scaling down
  else if (renderWidth < tableWidth && !isTableWidthFixed) {
    scaleDownPercent = calcScalePercent({
      renderWidth,
      tableWidth,
      maxScale: MAX_SCALING_PERCENT
    });
  }
  return targetWidths.map(colWidth => {
    const width = fixColumnWidth(colWidth, minTableWidth, maxTableWidth, zeroWidthColumnsCount, scaleDownPercent) || cellMinWidth;
    const style = width ? {
      width: `${width}px`
    } : {};
    return style;
  });
};
export const Colgroup = props => {
  let {
    columnWidths,
    isNumberColumnEnabled
  } = props;
  const flags = useFeatureFlags();
  if (!columnWidths) {
    return null;
  }
  const colStyles = renderScaleDownColgroup({
    ...props,
    isTableScalingEnabled: !!(flags && 'tablePreserveWidth' in flags && flags.tablePreserveWidth)
  });
  if (!colStyles) {
    return null;
  }
  return /*#__PURE__*/React.createElement("colgroup", null, isNumberColumnEnabled && /*#__PURE__*/React.createElement("col", {
    // eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766
    style: {
      width: akEditorTableNumberColumnWidth
    },
    "data-test-id": 'num'
  }), colStyles.map((style, idx) =>
  /*#__PURE__*/
  // eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766
  React.createElement("col", {
    key: idx,
    style: style
  })));
};